import { useState, type ReactNode } from 'react';
import { Button } from '../../design-system';
import styles from './SubAreaName.module.css';

/**
 * El nombre de una sub área en su tarjeta, y cómo se cambia.
 *
 * Lo pidió Enrique el 22 oct 2026: para corregir un nombre había que eliminar la
 * sub área y crearla otra vez, y eliminarla sólo se puede con nadie dentro. El
 * servidor ya aceptaba el cambio; la tarjeta no lo ofrecía.
 *
 * Se edita en su sitio y no en un diálogo: es una palabra, y el nombre nuevo se
 * lee donde va a quedar. Enter guarda y Escape deja el que tenía —sin cerrar la
 * pantalla, que también escucha Escape—. Si el servidor lo rechaza —otra sub
 * área ya se llama así— el campo sigue abierto con lo escrito, para corregirlo.
 */
export const SubAreaName = ({
  name,
  count,
  onRename,
}: {
  readonly name: string;
  readonly count: number;
  /** Devuelve si quedó guardado; quien llama dice por qué no. */
  readonly onRename: (next: string) => Promise<boolean>;
}): ReactNode => {
  const [draft, setDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const people = (
    <span className={styles.count}>
      {count} {count === 1 ? 'persona' : 'personas'}
    </span>
  );

  if (draft === null) {
    return (
      <span className={styles.text}>
        <span className={styles.nameRow}>
          <span className={styles.name}>{name}</span>
          <button
            type="button"
            className={styles.rename}
            aria-label={`Renombrar ${name}`}
            onClick={() => {
              setDraft(name);
            }}
          >
            Renombrar
          </button>
        </span>
        {people}
      </span>
    );
  }

  const next = draft.trim();
  const unchanged = next === name.trim();

  const save = async (): Promise<void> => {
    if (next.length === 0) return;
    if (unchanged) {
      setDraft(null);
      return;
    }
    setBusy(true);
    const saved = await onRename(next);
    setBusy(false);
    if (saved) setDraft(null);
  };

  return (
    <span className={styles.text}>
      <input
        type="text"
        className={styles.input}
        value={draft}
        maxLength={120}
        aria-label={`Nombre nuevo de ${name}`}
        autoFocus
        // Seleccionado al abrir: lo normal es escribir uno nuevo, no añadirle al viejo.
        onFocus={(event) => {
          event.currentTarget.select();
        }}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            void save();
          }
          if (event.key === 'Escape') {
            event.stopPropagation();
            setDraft(null);
          }
        }}
      />
      <span className={styles.actions}>
        <Button
          small
          busy={busy}
          disabled={next.length === 0}
          onClick={() => {
            void save();
          }}
        >
          Guardar
        </Button>
        <Button
          small
          variant="secondary"
          disabled={busy}
          onClick={() => {
            setDraft(null);
          }}
        >
          Cancelar
        </Button>
      </span>
      {people}
    </span>
  );
};
